import React, {createContext, useContext} from 'react'

interface StorageLevelsRouteContextValue {
  /**
   * Get the path for navigating to stock page (silos or materials).
   * Handles both HCEM absolute paths and Suite relative paths.
   */
  getStockPath: (subPath: 'silos' | 'materials') => string

  /**
   * Get the path for navigating to materials list from material orders detail.
   * Handles both HCEM absolute paths and Suite relative paths.
   */
  getMaterialsPath: () => string

  /**
   * Get the path for navigating to silos from material orders detail.
   * Handles both HCEM absolute paths and Suite relative paths.
   */
  getSilosPath: () => string
}

const StorageLevelsRouteContext = createContext<StorageLevelsRouteContextValue | undefined>(
  undefined
)

export interface StorageLevelsRouteProviderProps {
  children: React.ReactNode
  pathGenerators: StorageLevelsRouteContextValue
}

/**
 * Provider that supplies route path generation callbacks for the storage-levels feature.
 * Parent wrappers pass route generators appropriate for their host app context (HCEM vs Suite).
 *
 * @example
 * // For HCEM planner (absolute paths):
 * <StorageLevelsRouteProvider pathGenerators={{
 *   getStockPath: (sub) => `/plants/${plantCode}/stock/${sub}`,
 *   getMaterialsPath: () => `/plants/${plantCode}/stock/materials`,
 *   getSilosPath: () => `/plants/${plantCode}/stock/silos`,
 * }}>
 *
 * @example
 * // For Suite (relative paths):
 * <StorageLevelsRouteProvider pathGenerators={{
 *   getStockPath: (sub) => `../${sub}`,
 *   getMaterialsPath: () => `../materials`,
 *   getSilosPath: () => `../silos`,
 * }}>
 */
export const StorageLevelsRouteProvider: React.FC<StorageLevelsRouteProviderProps> = ({
  children,
  pathGenerators
}) => {
  return (
    <StorageLevelsRouteContext.Provider value={pathGenerators}>
      {children}
    </StorageLevelsRouteContext.Provider>
  )
}

/**
 * Hook to access route path generation callbacks.
 * Use to build navigation paths without needing to know host app context.
 */
export const useStorageLevelsRoute = (): StorageLevelsRouteContextValue => {
  const context = useContext(StorageLevelsRouteContext)
  if (context === undefined) {
    throw new Error('useStorageLevelsRoute must be used within StorageLevelsRouteProvider')
  }
  return context
}
